import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';

/**
 * The semantic remix request built for an accepted `SUGGESTED_REMIX` suggestion. These are the
 * "what to generate" bits the backend supplies; the remix engine fills in the runtime/environment
 * context (live content snapshot, space, product, etc.) itself.
 *
 * Declared locally (rather than importing `RemixModalMetadata`) so this package stays independent of
 * `@atlassian/editor-plugin-malleable-ui`. The engine wiring — and that dependency — lands in a
 * follow-up.
 */
export type SuggestedRemixMetadata = {
	aspectRatio?: string | null;
	dynamicUiSubtype?: string;
	dynamicUiType?: string;
	remixSource: 'suggestedRemix';
	userPrompt?: string;
};

/**
 * A fully-resolved suggested-remix request: the anchor `localIds` plus the metadata the remix
 * engine consumes. `triggerSuggestedRemix` returns this so the follow-up engine wiring (and tests)
 * can drive generation without re-deriving anything.
 */
export type SuggestedRemixRequest = {
	localIds?: string[];
	metadata: SuggestedRemixMetadata;
};

/**
 * Maps the backend-supplied `creationContext` + prompt onto the metadata the remix engine consumes.
 * Pure and serialisable, so it can be unit-tested without standing up an editor.
 */
export function buildSuggestedRemixMetadata({
	creationContext,
	userPrompt,
}: {
	creationContext?: Record<string, unknown>;
	userPrompt?: string;
}): SuggestedRemixMetadata {
	const context = creationContext ?? {};
	const additionalContext = context.additionalContext as
		| { aspectRatio?: string | null }
		| undefined;

	return {
		userPrompt,
		dynamicUiType: context.dynamicUiType as string | undefined,
		dynamicUiSubtype: context.dynamicUiSubtype as string | undefined,
		aspectRatio: additionalContext?.aspectRatio,
		remixSource: 'suggestedRemix',
	};
}

/**
 * Resolves an accepted `SUGGESTED_REMIX` suggestion into a remix request. The backend supplies the
 * semantic bits (`creationContext.dynamicUiType` / `dynamicUiSubtype`, `userPrompt`, anchor
 * `localIds`); the remix engine owns generation, the in-editor thinking bar, insertion and cleanup.
 *
 * Actually driving the engine requires `@atlassian/editor-plugin-malleable-ui`, which this package
 * does not yet depend on — so this currently just resolves and returns the request. The follow-up
 * adds that dependency (as an `OptionalPlugin`) and turns the returned request into a single call:
 *
 * ```ts
 * // localId resolved from request.localIds
 * api.malleableUi?.commands.startRemix({ localId, remixModalMetadata: request.metadata });
 * ```
 *
 * Returns `undefined` when there is no editor API to drive (keeps the plugin usable everywhere).
 */
export function triggerSuggestedRemix({
	api,
	creationContext,
	localIds,
	userPrompt,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	creationContext?: Record<string, unknown>;
	localIds?: string[];
	userPrompt?: string;
}): SuggestedRemixRequest | undefined {
	if (!api?.core) {
		return undefined;
	}

	return {
		localIds,
		metadata: buildSuggestedRemixMetadata({ creationContext, userPrompt }),
	};
}
